/**
 * UUIDv7 — RFC 9562. Ідентифікатор, який сортується за часом створення.
 *
 * Чому не UUIDv4: первинний ключ у SQLite — це B-дерево. Випадкові ключі
 * розкидають вставки по всьому дереву, і на базі щоденника за десять років
 * це перетворюється на постійне перетасування сторінок. v7 вставляється
 * майже завжди в кінець.
 *
 * Час і випадковість приходять аргументами, а не з глобальних `Date.now`
 * і `crypto`: інакше тест не має влади ні над порядком, ні над колізіями.
 */
import { formatUuid } from "./uuid.js";
/** 48 біт мілісекунд вистачає до 10889 року. */
const MAX_TIME = 2 ** 48 - 1;
/** Лічильник у полі rand_a: 12 біт, метод 2 з RFC 9562 §6.2. */
const MAX_COUNTER = 0xfff;
export class TimeOutOfRangeError extends Error {
    constructor(ms) {
        super(`час поза межами UUIDv7: ${ms}`);
        this.name = "TimeOutOfRangeError";
    }
}
/**
 * Генератор UUIDv7 із монотонністю в межах однієї мілісекунди.
 *
 * Без лічильника два ідентифікатори, створені в ту саму мілісекунду,
 * впорядковувалися б випадково — а на швидкому імпорті це саме той випадок,
 * коли порядок записів має значення.
 */
export function makeUuidV7(random) {
    let lastMs = -1;
    let counter = 0;
    return function next(nowMs = Date.now()) {
        const ms = Math.floor(nowMs);
        if (ms < 0 || ms > MAX_TIME)
            throw new TimeOutOfRangeError(ms);
        if (ms === lastMs) {
            counter += 1;
            if (counter > MAX_COUNTER) {
                // Понад 4096 ідентифікаторів за мілісекунду. Замість переповнення
                // позичаємо наступну мілісекунду: краще ідентифікатор «з майбутнього»
                // на 1 мс, ніж два однакових.
                lastMs += 1;
                counter = 0;
            }
        }
        else if (ms > lastMs) {
            lastMs = ms;
            counter = 0;
        }
        else {
            // Годинник пішов назад — переведення часу, синхронізація NTP. Порядок
            // важливіший за точність позначки, тому час не відкочуємо.
            counter += 1;
        }
        const bytes = new Uint8Array(16);
        const t = lastMs;
        bytes[0] = Math.floor(t / 2 ** 40) & 0xff;
        bytes[1] = Math.floor(t / 2 ** 32) & 0xff;
        bytes[2] = Math.floor(t / 2 ** 24) & 0xff;
        bytes[3] = Math.floor(t / 2 ** 16) & 0xff;
        bytes[4] = Math.floor(t / 2 ** 8) & 0xff;
        bytes[5] = t & 0xff;
        // версія 7 у старших чотирьох бітах, далі 12 біт лічильника
        bytes[6] = 0x70 | ((counter >> 8) & 0x0f);
        bytes[7] = counter & 0xff;
        const rand = random(8);
        for (let i = 0; i < 8; i++)
            bytes[8 + i] = rand[i] ?? 0;
        // варіант RFC 4122: старші два біти 10
        bytes[8] = (bytes[8] ?? 0) & 0x3f;
        bytes[8] = (bytes[8] ?? 0) | 0x80;
        return formatUuid(bytes);
    };
}
/** Час створення з UUIDv7. Потрібен для сортування без окремого стовпця. */
export function timeOfUuidV7(uuid) {
    const hex = uuid.replace(/-/g, "").slice(0, 12);
    if (!/^[0-9a-f]{12}$/.test(hex))
        throw new TimeOutOfRangeError(Number.NaN);
    return Number.parseInt(hex, 16);
}
//# sourceMappingURL=uuidv7.js.map